/**
 * Bond Library — inventory view of every Freddie / Fannie multifamily
 * deal Clarid has ingested. Answers the analyst's "where did my data go"
 * question after they upload an MSIA CSV / Fannie xlsx.
 *
 * Surfaces the readiness of each deal at a glance:
 *   - Whether the OCS prospectus has been parsed
 *   - Whether every property has a census tract resolved (geocoding)
 *   - Whether every property has an AMI affordability profile
 *
 * Click into a Deal → see all its bonds, loans, properties.
 *
 * Phase 2 will pre-populate this from the bulk monthly factor files
 * so RJ never sees an empty library — but for now this view shows
 * exactly what the analyst has uploaded plus anything Register-flow
 * created automatically.
 */

import { useState, useMemo, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BuildingLibraryIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

import { bondsApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';

// ─── Types (mirror backend response shape) ──────────────────────────────────

interface DealRow {
  dealId: string;
  agency: 'FREDDIE' | 'FANNIE';
  seriesPrefix: string;
  dealName: string;
  issueDate: string | null;
  maturityDate: string | null;
  currentBalance: string | null;
  ocsPdfUrl: string | null;
  ocsExtractionStatus: 'PARSED' | 'PENDING' | 'FAILED' | 'NOT_APPLICABLE';
  ocsExtractedAt: string | null;
  updatedAt: string;
  cusips: string[];
  bondCount: number;
  loanCount: number;
  propertyCount: number;
  geocodedCount: number;
  affordabilityCount: number;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatBalance(raw: string | null): string {
  if (!raw) return '—';
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  return `$${n.toLocaleString()}`;
}

function readinessClass(deal: DealRow): { label: string; color: string; icon: typeof CheckCircleIcon } {
  if (deal.ocsExtractionStatus === 'FAILED') return { label: 'OCS failed', color: 'text-red-700 bg-red-50', icon: XCircleIcon };
  if (deal.ocsExtractionStatus === 'PENDING') return { label: 'Pending', color: 'text-amber-700 bg-amber-50', icon: ExclamationTriangleIcon };
  if (deal.ocsExtractionStatus === 'NOT_APPLICABLE') return { label: 'N/A', color: 'text-gray-600 bg-gray-100', icon: ExclamationTriangleIcon };
  if (deal.propertyCount === 0) return { label: 'No properties', color: 'text-amber-700 bg-amber-50', icon: ExclamationTriangleIcon };
  if (deal.affordabilityCount < deal.propertyCount) return { label: 'Affordability missing', color: 'text-amber-700 bg-amber-50', icon: ExclamationTriangleIcon };
  if (deal.geocodedCount < deal.propertyCount) return { label: 'Geocoding incomplete', color: 'text-amber-700 bg-amber-50', icon: ExclamationTriangleIcon };
  return { label: 'Ready', color: 'text-green-800 bg-green-50', icon: CheckCircleIcon };
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function BondsLibraryPage() {
  const { teamId, isLoading: teamLoading } = useTeamId();
  const [search, setSearch] = useState('');
  const [agency, setAgency] = useState<'' | 'FREDDIE' | 'FANNIE'>('');
  const [statusFilter, setStatusFilter] = useState<'' | 'PARSED' | 'PENDING' | 'FAILED' | 'NOT_APPLICABLE'>('');

  const { data, isLoading } = useQuery({
    queryKey: ['bonds-library', teamId, search, agency, statusFilter],
    queryFn: () => bondsApi.listLibrary(teamId!, {
      search: search || undefined,
      agency: agency || undefined,
      status: statusFilter || undefined,
      limit: 100,
    }).then(r => r.data),
    enabled: !!teamId,
  });

  const deals: DealRow[] = useMemo(() => data?.deals ?? [], [data?.deals]);
  const total: number = data?.total ?? 0;

  const summary = useMemo(() => {
    const ready = deals.filter(d => readinessClass(d).label === 'Ready').length;
    const properties = deals.reduce((s, d) => s + d.propertyCount, 0);
    const affordability = deals.reduce((s, d) => s + d.affordabilityCount, 0);
    return { ready, total: deals.length, properties, affordability };
  }, [deals]);

  if (teamLoading) {
    return <div className="animate-pulse h-64 bg-gray-200 rounded-lg" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
            <BuildingLibraryIcon className="h-7 w-7 text-teal-600" />
            Bond Library
          </h1>
          <p className="text-gray-600 mt-1">
            Every Freddie / Fannie multifamily deal Clarid has ingested for this account, with readiness status.
          </p>
        </div>
        <Link
          to="/bonds"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 hover:text-teal-900"
        >
          <ArrowLeftIcon className="h-4 w-4" /> Back to analysis
        </Link>
      </div>

      {/* Summary */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Deals indexed" value={total.toLocaleString()} />
        <Stat label="Ready" value={summary.ready.toLocaleString()} />
        <Stat label="Properties" value={summary.properties.toLocaleString()} />
        <Stat label="With affordability data" value={summary.affordability.toLocaleString()} />
      </section>

      {/* Filters */}
      <section className="bg-white rounded-2xl border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              placeholder="Search by Deal ID, Deal Name, or CUSIP"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
          <select
            value={agency}
            onChange={(e) => setAgency(e.target.value as any)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="">All agencies</option>
            <option value="FREDDIE">Freddie</option>
            <option value="FANNIE">Fannie</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="">All statuses</option>
            <option value="PARSED">OCS parsed</option>
            <option value="PENDING">OCS pending</option>
            <option value="FAILED">OCS failed</option>
            <option value="NOT_APPLICABLE">N/A</option>
          </select>
        </div>
      </section>

      {/* Deals table */}
      <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-gray-500">Loading…</div>
        ) : deals.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-600 mb-2">No deals in your library yet.</p>
            <p className="text-sm text-gray-500">
              Drop an MSIA CSV or Fannie DUS Disclose xlsx on{' '}
              <Link to="/bonds" className="text-teal-700 hover:text-teal-900 font-medium">the analysis page</Link>{' '}
              to start ingesting.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Deal</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Agency</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Balance</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">CUSIPs</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Properties</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Geocoded</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Affordability</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((d) => {
                  const ready = readinessClass(d);
                  const Icon = ready.icon;
                  return (
                    <tr
                      key={d.dealId}
                      className="border-b border-gray-100 hover:bg-teal-50/30 cursor-pointer"
                    >
                      <td className="px-4 py-3">
                        <div className="font-semibold text-navy-900">{d.dealId}</div>
                        <div className="text-xs text-gray-500">{d.dealName !== d.dealId ? d.dealName : `${d.seriesPrefix} series`}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${
                          d.agency === 'FREDDIE' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                        }`}>
                          {d.agency}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">{formatBalance(d.currentBalance)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="font-medium">{d.bondCount}</div>
                        {d.cusips.length > 0 && d.cusips.length <= 2 && (
                          <div className="text-xs font-mono text-gray-500">
                            {d.cusips.join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">{d.propertyCount}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={d.geocodedCount === d.propertyCount ? 'text-green-700' : 'text-amber-700'}>
                          {d.geocodedCount}/{d.propertyCount}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={d.affordabilityCount === d.propertyCount && d.propertyCount > 0 ? 'text-green-700' : 'text-amber-700'}>
                          {d.affordabilityCount}/{d.propertyCount}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${ready.color}`}>
                          <Icon className="h-3.5 w-3.5" /> {ready.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {total > deals.length && (
              <div className="px-4 py-3 text-xs text-gray-500 border-t border-gray-100 bg-gray-50">
                Showing {deals.length} of {total} — refine the search to narrow further.
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Sub-component ──────────────────────────────────────────────────────────

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold text-navy-900 mt-1">{value}</div>
    </div>
  );
}
